import { apiRequest } from "./api-request";

// Read access to the Assistant's chat history via the BFF /ai/sessions
// passthrough (agent-service GET /agent/sessions[/{id}]). Upstream calls these
// "sessions"; in Spark they are agent threads (see CONTEXT.md). GET reads — no
// CSRF header needed, just the session cookie.

import type { FilterDescription } from "@/lib/agent-agui";

export interface AgentThreadRef {
  id: string;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
  preview: string | null;
}

export interface AgentThreadMessage {
  // `info` is a reloaded profile load (a synthetic turn, @adr SPARK-0014-design-primitives-single-source@1) — rendered as a
  // distinct info block rather than a chat bubble.
  role: "user" | "assistant" | "info";
  content: string;
  // Section the turn was answered in (save/profile, growth, … ; "default" = uncolored) — mapped
  // to the bubble border color. Tour steps additionally carry the outline structure + revisit
  // target.
  section?: string;
  indent?: number | null;
  anchor?: string | null;
  route?: string | null;
  // The answer carries public-web content (a WebSearch turn); absent or false otherwise.
  externalSources?: boolean;
  // What the assistant did to produce this answer, kept server-side (agent_subgraph_steps) and
  // replayed here so a reloaded thread can still show it. Absent on turns answered before
  // agent-service began keeping them.
  thinking?:
    | { phase: string; params?: Record<string, string>; depth?: number; filter?: FilterDescription }[]
    | null;
  noticeCode?: string | null;
}

/** One structured tour step sent for persistence (mirrors the server's TourStepRecord). */
export interface TourStepPayload {
  content: string;
  indent: number;
  section: string;
  anchor?: string;
  route?: string;
}

/** Where this thread's interrupted tour stopped — set iff its LAST tour turn is unfinished. */
export interface ResumeTourRef {
  command: string;
  route?: string | null;
  anchor?: string | null;
}

export interface AgentThreadDetail {
  id: string;
  createdAt: string;
  updatedAt: string;
  messages: AgentThreadMessage[];
  // The last reply's follow-up suggestions, restored as chips when reopening the thread.
  suggestions?: string[];
  resumeTour?: ResumeTourRef | null;
}

export class AgentThreadError extends Error {}

export async function listAgentThreads(signal?: AbortSignal): Promise<AgentThreadRef[]> {
  const { response } = await apiRequest("ai/sessions", {
    headers: { Accept: "application/json" },
    signal,
  });
  if (!response.ok) throw new AgentThreadError(`Failed to list chats (${response.status})`);
  return (await response.json()) as AgentThreadRef[];
}

export async function fetchAgentThread(
  id: string,
  signal?: AbortSignal,
): Promise<AgentThreadDetail> {
  const { response } = await apiRequest(`ai/sessions/${encodeURIComponent(id)}`, {
    headers: { Accept: "application/json" },
    signal,
  });
  if (!response.ok) throw new AgentThreadError(`Failed to load chat (${response.status})`);
  return (await response.json()) as AgentThreadDetail;
}

// Persist a guided tour (/tour, /tour-all) as a synthetic turn on the thread, so the walkthrough
// survives reload and the assistant knows what the tour showed. Mutating → CSRF.
export async function postTourTranscript(
  id: string,
  command: string,
  transcript: string,
  steps: TourStepPayload[] = [],
  // keepalive: beacon-style send that survives page unload (pagehide mid-tour). sendBeacon
  // itself can't carry the CSRF header, keepalive fetch can. Bodies stay far below the 64KB
  // keepalive cap (transcript ≤20KB, real tours a few KB).
  // finished=false marks an interruption: the thread then offers to resume on reload.
  opts: { keepalive?: boolean; finished?: boolean } = {},
): Promise<void> {
  const { response } = await apiRequest(`ai/sessions/${encodeURIComponent(id)}/tour`, {
    method: "POST",
    csrf: true,
    keepalive: opts.keepalive ?? false,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ command, transcript, steps, finished: opts.finished ?? true }),
  });
  if (!response.ok) throw new AgentThreadError(`Failed to store tour (${response.status})`);
}

/**
 * Stop the turn `runId` is running.
 *
 * A SECOND request while the turn's stream is still open: aborting that stream stops the listening,
 * not the work — agent-service runs the loop to completion on client disconnect. Upstream answers
 * 204 whether or not anything was running, so a stop that races a finishing turn is not an error;
 * a transport failure is swallowed for the same reason, because the user's intent is already
 * expressed locally by abandoning the stream.
 */
export async function stopAgentRun(runId: string): Promise<void> {
  await apiRequest(`ai/runs/${encodeURIComponent(runId)}/stop`, {
    method: "POST",
    csrf: true,
  }).catch(() => undefined);
}

// The /tour-all onboarding hint (topbar help button): whether to show it, and what the user did
// with it. The server log retires the start-tour nag after dismiss, a tour start, or enough
// timeouts, and remembers the highest "what's new" version the user saw or dismissed.
export type TourHintEvent = "dismissed" | "started" | "timeout" | "news-seen";

export interface TourHintState {
  show: boolean;
  /** Whether the user ever actually started a tour (drives the what's-new auto-pick). */
  started?: boolean;
  /** Highest what's-new version seen/dismissed; absent or null = none. */
  newsVersion?: string | null;
}

export async function fetchTourHint(signal?: AbortSignal): Promise<TourHintState> {
  const { response } = await apiRequest("ai/tour-hint", {
    headers: { Accept: "application/json" },
    signal,
  });
  if (!response.ok) throw new AgentThreadError(`Failed to load tour hint (${response.status})`);
  return (await response.json()) as TourHintState;
}

export async function postTourHintEvent(event: TourHintEvent, version?: string): Promise<void> {
  const { response } = await apiRequest("ai/tour-hint", {
    method: "POST",
    csrf: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(version === undefined ? { event } : { event, version }),
  });
  if (!response.ok) throw new AgentThreadError(`Failed to store tour hint event (${response.status})`);
}

// DELETE is mutating, so it carries the CSRF token (the GET reads don't).
export async function deleteAgentThread(id: string, signal?: AbortSignal): Promise<void> {
  const { response } = await apiRequest(`ai/sessions/${encodeURIComponent(id)}`, {
    method: "DELETE",
    csrf: true,
    signal,
  });
  if (!response.ok) throw new AgentThreadError(`Failed to delete chat (${response.status})`);
}
